import React, { useState, useEffect, useRef } from 'react';
import { cn, getRatioClassnames } from '@/lib/utils/cn';

type WorkflowItem = {
  content: React.ReactNode;
  title: string;
  subtitle: string;
  variant?: 'default' | 'primary' | 'success' | 'warning';
};

type WorkflowScrollSectionProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  items: WorkflowItem[];
  className?: string;
  bgColor?: string;
  topRight?: React.ReactNode;
  ratio?: string;
};

const getVariantClasses = (variant?: string) => {
  switch (variant) {
    case 'primary':
      return 'text-primary';
    case 'success':
      return 'text-green-600';
    case 'warning':
      return 'text-orange-600';
    default:
      return 'text-gray-900';
  }
};

const WorkflowScrollSection: React.FC<WorkflowScrollSectionProps> = ({
  title,
  subtitle,
  items,
  className = '',
  bgColor = '',
  topRight,
  ratio = '2:1',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [leftClass, rightClass] = getRatioClassnames(ratio);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || isMobile) return;

      const { top, height } = sectionRef.current.getBoundingClientRect();
      const scrollProgress = Math.max(0, Math.min(1, -top / (height - window.innerHeight)));

      const newIndex = Math.min(items.length - 1, Math.floor(scrollProgress * items.length));
      setActiveIndex(newIndex);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [items.length, isMobile]);

  if (isMobile) {
    return (
      <section className={cn('relative py-16', bgColor, className)}>
        <div className="mx-auto max-w-screen-xl w-full px-4 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
            <div className={cn('min-w-0', leftClass)}>
              {title && <h2 className="mb-4 text-3xl font-extrabold leading-none tracking-tight text-gray-900 md:text-4xl">{title}</h2>}
              {subtitle && <p className="mb-8 max-w-2xl">{subtitle}</p>}
            </div>
            {topRight && <div className={cn('mt-4 md:mt-0 md:ml-8 flex-shrink-0', rightClass)}>{topRight}</div>}
          </div>
          <div className="space-y-12">
            {items.map((item, index) => (
              <div key={index} className="space-y-6 flex flex-col">
                {/* Row 1: Content */}
                <div className="flex justify-center order-2">{item.content}</div>

                {/* Row 2: Stepper */}
                <div className="flex justify-center order-1 !mb-5 ">
                  <div className="w-8 h-8 rounded-full bg-primary border-4 border-primary text-white flex items-center justify-center">
                    <span className="text-sm font-bold">{index + 1}</span>
                  </div>
                </div>

                {/* Row 3: Title + Subtitle */}
                <div className="text-left order-3">
                  <h3 className={cn('text-xl font-bold mb-2', getVariantClasses(item.variant))}>{item.title}</h3>
                  <p className="text-gray-600">{item.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className={cn('relative', bgColor, className)} style={{ minHeight: `${items.length * 100}vh` }}>
      <div className="sticky top-0 h-screen flex items-center py-16">
        <div className="mx-auto max-w-screen-xl w-full px-4 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
            <div className={cn('min-w-0', leftClass)}>
              {title && <h2 className="mb-4 text-3xl font-extrabold leading-none tracking-tight text-gray-900 md:text-4xl">{title}</h2>}
              {subtitle && <p className="mb-8 max-w-2xl">{subtitle}</p>}
            </div>
            {topRight && <div className={cn('mt-4 md:mt-0 md:ml-8 flex-shrink-0', rightClass)}>{topRight}</div>}
          </div>
          {/* Row 1: Content */}
          <div className="grid grid-cols-3 gap-8 md:gap-12 mb-8 order-2">
            {items.map((item, index) => (
              <div
                key={index}
                className={cn(
                  'flex justify-center transition-all duration-500',
                  activeIndex !== index ? 'opacity-30 scale-95' : 'opacity-100 scale-100'
                )}
              >
                {item.content}
              </div>
            ))}
          </div>

          {/* Row 2: Stepper */}
          <div className="relative mb-12 w-[75%] mx-auto order-1">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-300 -translate-y-1/2" />
            <div
              className="absolute top-1/2 left-0 h-1 bg-primary -translate-y-1/2 transition-all duration-500"
              style={{
                width:
                  items.length === 1
                    ? '100%'
                    : activeIndex === 0
                    ? '0%'
                    : activeIndex === items.length - 1
                    ? '100%'
                    : `${(activeIndex / (items.length - 1)) * 100}%`,
              }}
            />
            <div className="relative flex justify-between">
              {items.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'w-8 h-8 rounded-full border-4 transition-all duration-500 flex items-center justify-center',
                    activeIndex >= index ? 'bg-primary border-primary text-white' : 'bg-white border-gray-300 text-gray-400'
                  )}
                >
                  <span className="text-sm font-bold">{index + 1}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Row 3: Title + Subtitle */}
          <div className="grid grid-cols-3 gap-8 md:gap-12 order-3">
            {items.map((item, index) => (
              <div key={index} className={cn('text-left transition-all duration-500', activeIndex !== index ? 'opacity-30' : 'opacity-100')}>
                <h3 className={cn('text-xl font-bold mb-2', getVariantClasses(item.variant))}>{item.title}</h3>
                <p className="text-gray-600">{item.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowScrollSection;
